import { FC, memo, useState, useEffect, useContext, useRef, useCallback } from 'react';

import { ToggleDialog } from '../../../../components/Tables/TableWithModalEdit/modalFormPieces/IFormModal';
import { Voucher } from '../../../../api/vouchers/types';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { fetchTransactionStatus } from '../../../../components/InnerPaymentResult/api';
import {
    buildReceiptCardDetails,
    dollarLinkedDealInUsd,
    isDollarLinkedDeal
} from '../../../../api/vouchers/cardDetails';
import {
    LINKED_SPLIT_PENDING_STATUS,
    LINKED_SPLIT_UNCONFIRMED_MESSAGE
} from '../../../../api/pelecardUtils/pelecardInstallments';
import PaymentIframeModal from '../../../../components/IframePayment/PaymentIframeModal';
import { displayToast } from '../../../../components/RTToast';

// localStorage key the app-wide CardRemittanceResume handler looks for after the
// gateway returns to the former page, to know a Remittance charge just completed
// and the document should be issued. Kept here so both sides agree on the name.
export const PENDING_CARD_REMITTANCE_KEY = 'pendingCardRemittance';

// localStorage key holding the in-app route (pathname + search + hash) the user
// was on before the Masleka payment. Pelecard does not reliably return to the
// deep GoodURL we pass — it lands the window on the app root — so the resume
// handler reads this and navigates back to the former page itself.
export const CARD_REMITTANCE_FORMER_PAGE_KEY = 'cardRemittanceFormerPage';

// localStorage key telling the resume handler WHICH document to issue after the
// card charge — a Remittance or a Receipt. Both flows share this sub-form, this
// pending state, and the app-wide resume handler; only the document differs.
export const CARD_DOCUMENT_TYPE_KEY = 'cardDocumentType';
export type CardDocumentType = 'REMITTANCE' | 'RECEIPT';

const MAX_INLINE_POLL_ATTEMPTS = 20;
// A dollar-linked charge is a server-side call that may take up to a minute
// (TOKEN_DEBIT_TIMEOUT_MS) — wait past it before calling the charge unconfirmed.
const MAX_LINKED_SPLIT_PENDING_POLLS = 60;

type Props = {
    open: boolean;
    toggleOpen: ToggleDialog;
    error?: string;
    title?: string;
    isLoading: boolean;
    values: any;
    documentGenerationData?: Partial<Voucher> | null;
    // Card-charge resume flow. Pelecard returns by redirecting the WHOLE window
    // (not the iframe) to the former page, so the document can't be issued from
    // memory afterwards. When either flag is set, we send the gateway back to the
    // former page and drop a flag + the payload so the app-wide resume handler can
    // issue the document and open its PDF once the page reloads. The two flags
    // pick which document the resume handler issues.
    resumeRemittance?: boolean;
    resumeReceipt?: boolean;
    selectedDocuments?: Array<Voucher> | null;
    // The travel file's trip start date (see usePaymentPlanInfo) — required
    // server-side to resolve whether the instalment/credit-deal plan the agent
    // picked (values.paymentMethodObject.numberOfPayments/isCreditDeal/
    // creditInstallments) is actually allowed for this trip.
    tripDate?: string | null;
    // Inline card flow. When provided, the payment is initialised with
    // FeedbackOnTop=False so Pelecard's redirect stays INSIDE the iframe and the
    // app window is never reloaded. On a server-verified approval this is called
    // with the form values so the parent can issue the document (and open its
    // PDF) in place. Supplying this switches the sub-form off the legacy
    // reload-and-resume path.
    onPaymentSuccess?: (values: any) => void;
};

const PreReceiptModalLevel2: FC<Props> = (props: Props) => {
    const {
        open = true,
        toggleOpen,
        error,
        title = '',
        isLoading,
        values,
        documentGenerationData,
        resumeRemittance = false,
        resumeReceipt = false,
        selectedDocuments = null,
        tripDate = null,
        onPaymentSuccess
    } = props;
    // Either resume flag routes through the persist-then-redirect path; the
    // document type distinguishes what the resume handler ultimately issues.
    const isResume = resumeRemittance || resumeReceipt;
    const resumeDocumentType: CardDocumentType = resumeReceipt ? 'RECEIPT' : 'REMITTANCE';
    // Presence of a success callback means the caller wants the in-place (no
    // reload) flow. It takes precedence over the legacy resume path.
    const inlineResult = !!onPaymentSuccess;
    // `values` (Formik's values object) and `toggleOpen`/`onPaymentSuccess`
    // (usually inline arrow functions from the parent) get a new reference on
    // nearly every render. finalizeInline needs their LATEST value without
    // depending on them directly — depending on them made its identity
    // unstable, which tore down and rebuilt the poll interval below on every
    // parent render instead of letting it tick every 1.5s as intended. Refs
    // updated during render (safe: they don't trigger re-renders or read stale
    // data) sidestep that without needing an extra effect.
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const toggleOpenRef = useRef(toggleOpen);
    toggleOpenRef.current = toggleOpen;
    const onPaymentSuccessRef = useRef(onPaymentSuccess);
    onPaymentSuccessRef.current = onPaymentSuccess;
    const [creditUrl, setCreditUrl] = useState<string>('');
    // Inline flow: once the charge is confirmed we blank the iframe behind a
    // loader so Pelecard's own success/result page (rendered inside the iframe
    // before it redirects to our result page) never flashes into view.
    const [finalizing, setFinalizing] = useState<boolean>(false);
    const currenciesContext = useContext(CurrenciesContext);

    // Inline flow bookkeeping: the transaction id to poll, and guards so the
    // document is issued exactly once across the poll + postMessage triggers.
    const transactionIdRef = useRef<string>('');
    const paymentsTerminalIdRef = useRef<string>('');
    const finishedRef = useRef<boolean>(false);
    const finalizingRef = useRef<boolean>(false);
    // Counts pending polls (no definitive StatusCode yet) so finalizeInline can  give up after MAX_INLINE_POLL_ATTEMPTS instead of polling forever.
    const pollAttemptsRef = useRef<number>(0);
    // Polls answered "dollar-linked charge still in progress".
    const linkedSplitPollsRef = useRef<number>(0);
    // Counts iframe navigations. The first load is the Pelecard payment form;
    // any later load means Pelecard navigated after the charge (its result page
    // or our redirect), which is our cue to blank the iframe so its result page
    // never flashes into view.
    const iframeLoadCountRef = useRef<number>(0);

    // The page the user was on before the Masleka payment iframe opened. Captured
    // on mount because the modal never changes the URL, so this stays the "former
    // page" the gateway returns to once the card is charged.
    //
    // Kept CLEAN — origin + pathname only, no query string or hash. Pelecard
    // mangles/drops long or complex query params in its GoodURL (same reason the
    // tour-registration/booking flows send a clean redirect and restore state
    // from storage), which was landing the window somewhere other than the former
    // page. The resume handler rebuilds everything from localStorage + Redis, so
    // the URL needs nothing beyond the route path.
    const formerPageUrl = useRef<string>(`${window.location.origin}${window.location.pathname}`);

    // The in-app route (path + query + hash) to return to. Pelecard does not
    // reliably honor the deep GoodURL, so the resume handler navigates back here
    // itself after the window reloads onto whatever page the gateway chose.
    const formerPagePath = useRef<string>(
        `${window.location.pathname}${window.location.search}${window.location.hash}`
    );

    const storeRedisObject = (id: string, values: any) => {
        // selectedDocuments is only relevant to the (multi-doc) remittance flow;
        // it is null for the receipt flow and simply omitted there.
        const body = JSON.stringify({
            key: id,
            value: { values, documentGenerationData, selectedDocuments }
        });
        fetch('/store-redis-object', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body
        });
    };

    useEffect(() => {
        const initPaymentGW = async () => {
            try {
                const response = await fetch(`/get-url`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                const url = await response.json();
                // Resolve the currency selected on the calling form (Receipt /
                // Remittance) to its code (e.g. "USD"). values.currency may be a
                // currency DB _id, a full Currency object, or already a code —
                // getCodeByCurrency normalises all of these. The backend expects
                // a code string; passing a raw _id silently defaulted to ILS.
                const currencyCode =
                    currenciesContext.getCodeByCurrency(values?.currency ?? null) || values?.currency;
                // The terminal the agent picked on the Receipt/Remittance form.
                // Send its id: resolvePelecardTerminal looks the row up server-side
                // and charges through that exact terminal. classId rides along as
                // the fallback it always was — a terminal that serves no particular
                // class has classId NULL, and resolving by class alone then quietly
                // charged through the DEFAULT terminal instead of the chosen one.
                const paymentsTerminalId = values?.paymentMethodObject?.paymentsTerminal?._id ?? undefined;
                const classId = values?.paymentMethodObject?.paymentsTerminal?.classId ?? undefined;
                // Instalment / credit-deal plan the agent picked — see
                // CreditCardPaymentPlanFields. A credit deal is ILS-only (Pelecard's
                // manual), so it forces the CHARGE currency to ILS regardless of
                // what's selected above — the document itself keeps whatever
                // currency the agent chose for bookkeeping. A payment count above 1
                // is a dollar-linked split, only ever offered on a USD receipt: it
                // goes out in dollars and the server charges it in ₪ afterwards
                // (pelecardUtils/linkedSplit.ts).
                const numberOfPayments = Number(values?.paymentMethodObject?.numberOfPayments) || 1;
                const isCreditDeal = !!values?.paymentMethodObject?.isCreditDeal;
                const creditInstallments = values?.paymentMethodObject?.creditInstallments
                    ? Number(values.paymentMethodObject.creditInstallments)
                    : undefined;
                const isLinkedSplit = numberOfPayments > 1 && !isCreditDeal;
                const isSplitting = isCreditDeal;
                const isAlreadyIls = String(currencyCode).toUpperCase() === 'ILS';
                // The document's own rate is local-units-per-1-doc-currency-unit
                // whenever the document isn't already in the local (₪) currency —
                // the same conversion AmountCurrencyRateBox uses for its own
                // local-equivalent hint. Pelecard is ₪-only, and this tenant's local
                // currency is ₪ whenever Pelecard is in play at all.
                const chargePrice =
                    isSplitting && !isAlreadyIls
                        ? Math.round((Number(values?.amount) || 0) * (Number(values?.rate) || 1) * 100) / 100
                        : values?.amount;
                const chargeCurrency = isSplitting ? 'ILS' : currencyCode;
                const body = JSON.stringify({
                    price: chargePrice,
                    language: 'en',
                    currency: chargeCurrency,
                    startDate: tripDate ?? undefined,
                    // No trip date at all (a supplier/agent payment, or a
                    // non-tour service travel file) — the "N days before
                    // departure" gate has no trip to apply to, so it's
                    // skipped server-side rather than failing closed on a
                    // date that was never going to exist. See
                    // CreditCardPaymentPlanFields, which offers the same
                    // split/credit options under the same condition.
                    tripDateApplies: !!tripDate,
                    // The split is charged against the travel file, and this form
                    // saves its own receipt — without the flag the server would
                    // issue a second one after the charge.
                    ...(isLinkedSplit
                        ? { travelFileId: documentGenerationData?.travelFileId, clientCreatesReceipt: true }
                        : {}),
                    numberOfPayments,
                    isCreditDeal,
                    creditInstallments,
                    // Drives which Pelecard terminal the charge settles on — see note above.
                    paymentsTerminalId,
                    classId,
                    // Inline flow (Issue Documents Receipt / Remittance): tell the
                    // server to render Pelecard's redirect INSIDE the iframe
                    // (FeedbackOnTop=False) so the app window is never reloaded. The
                    // parent then verifies + issues the document in place. Falls back
                    // to the legacy resume/reload target otherwise.
                    //
                    // The redirect just needs a lightweight same-origin target that
                    // is NOT the app itself (the app has an SPA fallback, so any app
                    // route would boot the whole React app inside this 600px iframe).
                    // favicon.png is a tiny static asset that loads instantly; we
                    // never read its contents — the iframe's onLoad tells us Pelecard
                    // navigated after the charge, and the charge is confirmed by
                    // polling the transaction.
                    inlineResult,
                    redirectUrl: inlineResult
                        ? `${window.location.origin}/favicon.png`
                        : isResume
                          ? formerPageUrl.current
                          : `${url}`
                });
                const res = await fetch('/init-paymentGW', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body
                });
                if (res.ok) {
                    const responseData = await res.json();
                    if (responseData?.Error?.ErrCode === 0) {
                        const id = responseData.ConfirmationKey;
                        const url = new URLSearchParams(new URL(responseData.URL).search);
                        const transactionId = url.get('transactionId') || '';
                        localStorage.setItem('creditSessionId', id);
                        localStorage.setItem('creditTransactionId', transactionId);
                        // Verifying the transaction later has to name the SAME
                        // terminal it was charged on — the resume flow reloads the
                        // page, so component state is gone by then.
                        localStorage.setItem(
                            'creditPaymentsTerminalId',
                            String(responseData.paymentsTerminalId ?? '')
                        );
                        // Held for the inline poller / message handler below.
                        transactionIdRef.current = transactionId;
                        // Server-resolved terminal — the verify call must name the
                        // same one the charge settled on.
                        paymentsTerminalIdRef.current = String(responseData.paymentsTerminalId ?? '');
                        // Store userKey and totalInCents for post-payment validation
                        if (responseData.userKey) localStorage.setItem('creditUserKey', responseData.userKey);
                        if (responseData.totalInCents != null)
                            localStorage.setItem('creditTotalInCents', String(responseData.totalInCents));
                        // Legacy resume/reload flow only. In the inline flow the
                        // document is issued in place, so none of the post-reload
                        // resume state (pending flag, former page, redis payload) is
                        // needed.
                        if (isResume && !inlineResult) {
                            localStorage.setItem(CARD_REMITTANCE_FORMER_PAGE_KEY, formerPagePath.current);
                            localStorage.setItem(CARD_DOCUMENT_TYPE_KEY, resumeDocumentType);
                            localStorage.setItem(PENDING_CARD_REMITTANCE_KEY, '1');
                            console.info('[CardRemittance] persisted pending document before Masleka', {
                                documentType: resumeDocumentType,
                                formerPage: formerPagePath.current,
                                redisId: id,
                                transactionId
                            });
                        }
                        if (!inlineResult) storeRedisObject(id, values);
                        setCreditUrl(responseData.URL);
                    } else {
                        console.error('[CreditCardSubForm] Pelecard init rejected', responseData?.Error);
                        displayToast(
                            'error',
                            'Payment initialization failed',
                            'Could not start the card payment session. Please try again or contact support.'
                        );
                        toggleOpen();
                    }
                } else {
                    console.error('Error response:', res);
                    // The server says why when it refuses (e.g. "Payment amount (5680) exceeds the
                    // remaining balance (0)" on a booking already paid in full) — show that, not a
                    // generic "try again" that sends the user to support for nothing.
                    const reason = await res
                        .json()
                        .then((body: { error?: string }) => body?.error)
                        .catch(() => undefined);
                    displayToast(
                        'error',
                        'Payment initialization failed',
                        reason || 'Could not start the card payment session. Please try again or contact support.'
                    );
                    toggleOpen();
                }
            } catch (err) {
                console.log('Failed load credit screen', err);
                displayToast(
                    'error',
                    'Payment initialization failed',
                    'Could not start the card payment session. Please try again or contact support.'
                );
                toggleOpen();
            }
        };
        initPaymentGW();
        // Deliberately narrow: `values` is the Formik values object (a new
        // reference on every keystroke) and `currenciesContext`/`storeRedisObject`
        // aren't stable across renders either — reacting to the full dep set would
        // re-init the Pelecard session (and re-POST /init-paymentGW) on nearly every
        // render. Only the charged amount/currency should trigger a re-init.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.amount, values?.currency]);

    // Inline card flow: verify the charge server-side and, on approval, issue the
    // document in place (opening its PDF) without ever reloading the window. Fed
    // by both the iframe's onLoad (a fast path once Pelecard actually navigates)
    // and a polling fallback below, and guarded so it runs exactly once.
    const clearInlineKeys = () => {
        localStorage.removeItem('creditSessionId');
        localStorage.removeItem('creditTransactionId');
        localStorage.removeItem('creditUserKey');
        localStorage.removeItem('creditTotalInCents');
    };

    const finalizeInline = useCallback(async () => {
        if (!inlineResult || finishedRef.current || finalizingRef.current) return;
        const transactionId = transactionIdRef.current || localStorage.getItem('creditTransactionId') || '';
        if (!transactionId) return;
        finalizingRef.current = true;
        try {
            let deal;
            try {
                // Without the terminal the server falls back to the DEFAULT row,
                // and Pelecard finds no transaction for a charge taken elsewhere.
                deal = await fetchTransactionStatus(
                    transactionId,
                    paymentsTerminalIdRef.current ||
                        localStorage.getItem('creditPaymentsTerminalId') ||
                        undefined
                );
            } catch (transportErr) {
                console.error(
                    '[CreditCardSubForm] transient error verifying payment, will retry',
                    transportErr
                );
                setFinalizing(false);
                return;
            }

            // Bail out of the "keep polling" branches below once we've spent too
            // long without a definitive answer — otherwise a charge that never
            // resolves polls /payment-get-transaction forever.
            const giveUp = () => {
                finishedRef.current = true;
                setFinalizing(false);
                clearInlineKeys();
                displayToast(
                    'error',
                    'Payment status unknown',
                    'Could not verify the payment. Please refresh the page and check whether the card was charged before trying again.'
                );
                toggleOpenRef.current();
            };

            // A dollar-linked charge we cannot confirm. The money may have moved,
            // and this form — not the server — issues the receipt, so "try
            // again" would charge the card twice. Send the agent to check first.
            const giveUpUnconfirmedCharge = () => {
                finishedRef.current = true;
                setFinalizing(false);
                clearInlineKeys();
                displayToast(
                    'error',
                    'Installment charge not confirmed',
                    LINKED_SPLIT_UNCONFIRMED_MESSAGE
                );
                toggleOpenRef.current();
            };

            // No answer yet (payment in progress / transient gateway error) —
            // keep polling. Restore the iframe if we had blanked it on a benign
            // navigation (e.g. Pelecard re-rendering the form after a field error).
            // Only count this against the give-up cap once the iframe has actually
            // navigated past the initial card-entry form (iframeLoadCountRef >= 2)
            // — before that, "no answer" just means the customer is still typing
            // their card details, which can easily take longer than the cap.
            if (!deal || deal.StatusCode === undefined) {
                if (iframeLoadCountRef.current >= 2) {
                    pollAttemptsRef.current += 1;
                    if (pollAttemptsRef.current >= MAX_INLINE_POLL_ATTEMPTS) {
                        giveUp();
                        return;
                    }
                }
                setFinalizing(false);
                return;
            }

            // A dollar-linked split: the card check passed and the server is
            // charging it. Not a decline — keep polling, on its own budget: the
            // charge call may take up to a minute. An UNKNOWN charge is never
            // retried and needs a person.
            if (deal.StatusCode === LINKED_SPLIT_PENDING_STATUS) {
                linkedSplitPollsRef.current += 1;
                if (
                    deal.linkedSplitStatus === 'UNKNOWN' ||
                    linkedSplitPollsRef.current >= MAX_LINKED_SPLIT_PENDING_POLLS
                ) {
                    giveUpUnconfirmedCharge();
                    return;
                }
                setFinalizing(true);
                return;
            }

            // Approved.
            if (deal.StatusCode === '000') {
                // A dollar-linked charge must come with its dollar figures;
                // without them the receipt would print shekels as dollars.
                if (isDollarLinkedDeal(deal.ResultData) && !dollarLinkedDealInUsd(deal.ResultData)) {
                    giveUpUnconfirmedCharge();
                    return;
                }
                finishedRef.current = true;
                // Hide the iframe immediately so Pelecard's interstitial result
                // page is not visible while the document is issued.
                setFinalizing(true);
                clearInlineKeys();
                // Hand the document the card details off the approved deal. Only
                // the form values used to be passed, so the issued receipt had
                // nothing real to print — its card box was hardcoded blanks and
                // its instalment figures were placeholders. Same mapping the
                // Accounting-tab receipt and the tour-registration receipt use.
                // A dollar-linked charge reports shekels: the receipt takes its
                // dollar view, at the rate the shekels were fixed at.
                const linkedUsd = dollarLinkedDealInUsd(deal.ResultData);
                const cardDetails = buildReceiptCardDetails(linkedUsd?.deal ?? deal.ResultData);
                const values: any = valuesRef.current;
                onPaymentSuccessRef.current?.({
                    ...values,
                    ...(linkedUsd ? { rate: linkedUsd.rate } : {}),
                    paymentMethodObject: {
                        ...values?.paymentMethodObject,
                        ...cardDetails,
                        // A typed-in reference stands only if Pelecard gave none.
                        reference: cardDetails.reference || values?.paymentMethodObject?.reference || '',
                        approvalCode: deal.ResultData?.DebitApproveNumber || ''
                    }
                });
                toggleOpenRef.current();
                return;
            }

            if (iframeLoadCountRef.current < 2) {
                setFinalizing(false);
                return; // not submitted yet — keep polling silently, uncapped
            }
            finishedRef.current = true;
            clearInlineKeys();
            displayToast(
                'error',
                'Payment failed',
                deal?.ErrorMessage || 'The card charge was not approved.'
            );
            toggleOpenRef.current();
        } finally {
            finalizingRef.current = false;
        }
    }, [inlineResult]);

    // Fires on every iframe navigation. The initial load is the payment form;
    // on any subsequent load Pelecard has moved on after the charge (it redirects
    // the iframe to our favicon target), so blank the iframe at once (hiding any
    // gateway result page) and verify. A benign reload just un-blanks via the
    // pending paths in finalizeInline.
    const handleIframeLoad = useCallback(() => {
        if (!inlineResult) return;
        iframeLoadCountRef.current += 1;
        if (iframeLoadCountRef.current < 2 || finishedRef.current) return;
        setFinalizing(true);
        finalizeInline();
    }, [inlineResult, finalizeInline]);

    // Poll the transaction as the reliable completion signal: on approval issue
    // the document in place, on a real decline toast + close, otherwise keep
    // polling. Kept short so an approval is caught (and the iframe blanked) before
    // any gateway page is noticeable. Works alongside the onLoad blanking above —
    // this is the fallback that actually guarantees resolution when Pelecard
    // doesn't fire a second iframe onLoad (a 3-D Secure step or a client-side
    // navigation that doesn't trigger a fresh document load).
    useEffect(() => {
        if (!inlineResult) return;
        const intervalId = window.setInterval(() => {
            finalizeInline();
        }, 1500);
        return () => window.clearInterval(intervalId);
    }, [inlineResult, finalizeInline]);

    return (
        <PaymentIframeModal
            open={open}
            toggleOpen={toggleOpen}
            title={title}
            isLoading={isLoading}
            finalizing={finalizing}
            creditUrl={creditUrl}
            onIframeLoad={handleIframeLoad}
            error={error}
        />
    );
};

export default memo(PreReceiptModalLevel2);
